/**
 * QuotaErrorToast — custom Sonner renderer for quota / plan errors.
 *
 * Two-line layout: title + description with monospace numbers, plus an
 * optional CTA. Rendered via `toast.custom(...)` from `showQuotaErrorToast`.
 *
 * No router context inside Sonner's portal — the CTA falls back to
 * `window.location.assign(href)`. Fine for an error toast.
 */

import { toast } from 'sonner';

export interface QuotaToastContent {
  title: string;
  description: string;
  action?: { label: string; href: string };
}

interface Props extends QuotaToastContent {
  toastId: string | number;
}

export function QuotaErrorToast({ title, description, action, toastId }: Props) {
  return (
    <div className="flex w-full max-w-[360px] flex-col gap-1 rounded-lg border border-border-subtle bg-white px-4 py-3 shadow-soft-lg">
      <div className="text-sm font-semibold text-text">{title}</div>
      <div className="font-mono text-xs leading-relaxed text-text-secondary tabular-nums">
        {description}
      </div>
      {action && (
        <div className="mt-1.5 flex justify-end">
          <button
            type="button"
            onClick={() => {
              window.location.assign(action.href);
              toast.dismiss(toastId);
            }}
            className="text-xs font-medium text-primary hover:text-primary-hover"
          >
            {action.label}
          </button>
        </div>
      )}
    </div>
  );
}
